import React, { useEffect, useState, useMemo } from 'react';
import useInViewport from '../hooks/useInViewport';
import { trackEvent } from '../utils/analytics';
import '../styles/contact-section.css';

interface ContactMethod {
  id: string;
  icon: string;
  title: string;
  description: string;
  action: () => void;
  badge?: string;
  primary?: boolean;
}

const ContactSection: React.FC = () => {
  const { isInViewport, elementRef } = useInViewport({ threshold: 0.2 });
  const [animatedItems, setAnimatedItems] = useState<Set<string>>(new Set());
  const [hoveredMethod, setHoveredMethod] = useState<string | null>(null);

  const contactMethods: ContactMethod[] = useMemo(() => [
    {
      id: 'whatsapp',
      icon: 'fab fa-whatsapp',
      title: 'WhatsApp Business',
      description: 'Sofortige Antworten und direkter Support über WhatsApp Business',
      action: () => {
        trackEvent('Contact_WhatsApp_Click');
        window.open('https://wa.me/493042141523', '_blank');
      },
      primary: true
    },
    {
      id: 'email',
      icon: 'fas fa-envelope',
      title: 'E-Mail Anfrage',
      description: 'Detaillierte Projektbeschreibungen und professionelle Beratung',
      action: () => {
        trackEvent('Contact_Email_Click');
        window.location.href = 'mailto:info@stimm.digital';
      }
    },
    {
      id: 'livechat',
      icon: 'fas fa-comments',
      title: 'Live Support',
      description: 'Sofortige Hilfe durch unser Expertenteam im Live-Chat',
      badge: 'Online',
      action: () => {
        trackEvent('Contact_LiveChat_Click');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).$chatwoot) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).$chatwoot.toggle();
        }
      }
    }
  ], []);

  // Animation beim Scroll-In
  useEffect(() => {
    if (isInViewport) {
      const timeouts: ReturnType<typeof setTimeout>[] = [];
      
      contactMethods.forEach((method, index) => {
        const timeout = setTimeout(() => {
          setAnimatedItems(prev => new Set(prev).add(method.id));
        }, index * 150);
        timeouts.push(timeout);
      });

      return () => timeouts.forEach(clearTimeout);
    }
  }, [isInViewport, contactMethods]);

  const handleMethodHover = (methodId: string | null) => {
    setHoveredMethod(methodId);
  };

  return (
    <section 
      id="contact"
      ref={elementRef}
      className={`contact-section ${isInViewport ? 'in-viewport' : ''}`}
    >
      {/* Animated Background */}
      <div className="contact-background">
        <div className="contact-noise-overlay"></div>
        <div className="contact-gradient-blur"></div>
        <div className="contact-pattern-overlay"></div>
      </div>

      <div className="contact-container">
        {/* Header */}
        <div className={`contact-header ${animatedItems.size > 0 ? 'animated' : ''}`}>
          <span className="section-title">Kontakt</span>
          <h2>Lass uns sprechen</h2>
          <p className="section-subtitle">
            Hast du eine Idee oder ein konkretes Projekt? Schreib uns einfach oder ruf an. 
            Wir beraten dich gerne unverbindlich zu deinen Möglichkeiten.
          </p>
        </div>

        {/* Contact Methods Grid */}
        <div className="contact-methods-grid">
          {contactMethods.map((method, index) => (
            <div
              key={method.id}
              className={`contact-method ${method.primary ? 'primary' : ''} ${
                animatedItems.has(method.id) ? 'animated' : ''
              } ${hoveredMethod === method.id ? 'hovered' : ''}`}
              onClick={method.action}
              onMouseEnter={() => handleMethodHover(method.id)}
              onMouseLeave={() => handleMethodHover(null)}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {method.badge && (
                <span className="contact-method-badge">{method.badge}</span>
              )}
              
              <div className="contact-method-icon">
                <i className={method.icon}></i>
              </div>
              
              <div className="contact-method-content">
                <h3 className="contact-method-title">{method.title}</h3>
                <p className="contact-method-description">{method.description}</p>
              </div>
              
              <div className="contact-method-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
